/**
 * AiAssistantBlocks — рендер интерактивных карточек ответа агента.
 *
 * Типы блоков приходят с сервера (module ai_assistant): text, patient_card,
 * patient_list, doc_list, analysis_list, records, plan_list, plan, schedule,
 * confirm. Агент не присылает HTML — только данные, поэтому панель остаётся
 * безопасной.
 */
import { useTranslation } from "react-i18next";
import {
    IoCalendarOutline,
    IoChevronForwardOutline,
    IoDocumentTextOutline,
    IoFlaskOutline,
    IoFolderOpenOutline,
    IoPersonOutline,
    IoMedkitOutline,
    IoShieldCheckmarkOutline,
} from "react-icons/io5";

import { AiAssistantStepsDone } from "./AiAssistantSteps";

function CardHead({ title, subtitle }) {
    if (!title && !subtitle) return null;
    return (
        <div className="aia-card__head">
            {title ? <div className="aia-card__title">{title}</div> : null}
            {subtitle ? <div className="aia-card__subtitle">{subtitle}</div> : null}
        </div>
    );
}

function ClickRow({ children, onClick, disabled }) {
    if (!onClick || disabled) {
        return <div className="aia-row">{children}</div>;
    }
    return (
        <button type="button" className="aia-row aia-row--click" onClick={onClick}>
            {children}
            <IoChevronForwardOutline className="aia-row__arrow" size={14} />
        </button>
    );
}

function TextBlock({ block }) {
    return (
        <div className="aia-card aia-card--text">
            <CardHead title={block.title} />
            <div className="aia-card__text">{block.text}</div>
        </div>
    );
}

function PatientCard({ block }) {
    const { t } = useTranslation("aiAssistant");
    const data = block.data || {};
    const fields = Array.isArray(data.fields) ? data.fields : [];
    const doctors = Array.isArray(data.doctors) ? data.doctors : [];

    return (
        <div className="aia-card">
            <div className="aia-card__head">
                <div className="aia-card__title">{data.fio || t("blocks.patient")}</div>
                <div className="aia-card__subtitle">
                    {[data.birth, data.status, data.phone].filter(Boolean).join(" · ")}
                </div>
            </div>

            {data.diagnosis ? (
                <div className="aia-note">
                    <span className="aia-note__label">{t("blocks.diagnosis")}</span>
                    <span className="aia-note__value">{data.diagnosis}</span>
                </div>
            ) : null}

            {doctors.length ? (
                <div className="aia-note">
                    <span className="aia-note__label">{t("blocks.doctors")}</span>
                    <span className="aia-note__value">{doctors.join(", ")}</span>
                </div>
            ) : null}

            {fields.length ? (
                <dl className="aia-fields">
                    {fields.map((field) => (
                        <div className="aia-fields__row" key={field.code}>
                            <dt>{field.label}</dt>
                            <dd>{field.value}</dd>
                        </div>
                    ))}
                </dl>
            ) : null}
        </div>
    );
}

function PatientList({ block, onOpenPatient }) {
    const items = Array.isArray(block.items) ? block.items : [];
    return (
        <div className="aia-card">
            <CardHead title={block.title} subtitle={block.query} />
            <div className="aia-list">
                {items.map((item) => (
                    <ClickRow key={item.id} onClick={() => onOpenPatient?.(item.id)}>
                        <span className="aia-row__icon">
                            <IoPersonOutline size={16} />
                        </span>
                        <span className="aia-row__body">
                            <span className="aia-row__title">{item.fio}</span>
                            <span className="aia-row__meta">
                                {[item.birth, item.status].filter(Boolean).join(" · ")}
                            </span>
                        </span>
                    </ClickRow>
                ))}
            </div>
        </div>
    );
}

function DocList({ block, onOpenDoc }) {
    const items = Array.isArray(block.items) ? block.items : [];
    const patientId = Number(block.patient_id) || 0;
    return (
        <div className="aia-card">
            <CardHead title={block.title} subtitle={block.subtitle} />
            <div className="aia-list">
                {items.map((item) => (
                    <ClickRow
                        key={item.id}
                        onClick={
                            patientId && item.path
                                ? () => onOpenDoc?.(patientId, item.path, item.id)
                                : undefined
                        }>
                        <span className="aia-row__icon">
                            <IoDocumentTextOutline size={16} />
                        </span>
                        <span className="aia-row__body">
                            <span className="aia-row__title">{item.template_name}</span>
                            <span className="aia-row__meta">{item.date}</span>
                        </span>
                    </ClickRow>
                ))}
            </div>
        </div>
    );
}

function AnalysisList({ block }) {
    const items = Array.isArray(block.items) ? block.items : [];
    return (
        <div className="aia-card">
            <CardHead title={block.title} subtitle={block.subtitle} />
            <div className="aia-list">
                {items.map((item) => (
                    <div className="aia-row" key={item.id}>
                        <span className="aia-row__icon">
                            <IoFlaskOutline size={16} />
                        </span>
                        <span className="aia-row__body">
                            <span className="aia-row__title">{item.name}</span>
                            {item.preview ? (
                                <span className="aia-row__meta">{item.preview}</span>
                            ) : null}
                        </span>
                        <span className="aia-row__date">{item.date}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Records({ block }) {
    const rows = Array.isArray(block.rows) ? block.rows : [];
    return (
        <div className="aia-card">
            <CardHead title={block.title} subtitle={block.subtitle} />
            <div className="aia-table">
                {rows.map((row, index) => (
                    <div className="aia-table__row" key={`${row.name}-${index}`}>
                        <span className="aia-table__name">{row.name}</span>
                        <span className="aia-table__value">{row.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PlanList({ block, onOpenPlan }) {
    const items = Array.isArray(block.items) ? block.items : [];
    const patientId = Number(block.patient_id) || 0;
    return (
        <div className="aia-card">
            <CardHead title={block.title} subtitle={block.subtitle} />
            <div className="aia-list">
                {items.map((item) => (
                    <ClickRow
                        key={item.id}
                        onClick={
                            patientId ? () => onOpenPlan?.(patientId, item.id) : undefined
                        }>
                        <span className="aia-row__icon">
                            <IoFolderOpenOutline size={16} />
                        </span>
                        <span className="aia-row__body">
                            <span className="aia-row__title">{item.name}</span>
                            <span className="aia-row__meta">
                                {[item.status, item.date, item.total].filter(Boolean).join(" · ")}
                            </span>
                        </span>
                    </ClickRow>
                ))}
            </div>
        </div>
    );
}

function PlanView({ block }) {
    const data = block.data || {};
    const groups = Array.isArray(data.groups) ? data.groups : [];

    /** Приёмы приходят списком номеров; старый формат мог быть числом. */
    const appointmentsText = (value) => {
        if (Array.isArray(value)) return value.filter((n) => Number(n) > 0).join(", ");
        if (Number(value) > 0) return String(value);
        return "";
    };

    return (
        <div className="aia-card">
            <div className="aia-card__head">
                <div className="aia-card__title">{data.name}</div>
                <div className="aia-card__subtitle">
                    {[data.patient, data.status, data.date_created, data.total]
                        .filter(Boolean)
                        .join(" · ")}
                </div>
                {Number(data.count_appointment) > 0 ? (
                    <div className="aia-card__note">
                        Приёмов в плане: {data.count_appointment}
                    </div>
                ) : null}
            </div>
            {groups.map((group, index) => (
                <div className="aia-group" key={`${group.name}-${index}`}>
                    <div className="aia-group__title">
                        <IoMedkitOutline size={14} /> {group.name}
                        {Number(group.count_appointment) > 0 ? (
                            <span className="aia-group__count">
                                {group.count_appointment} приём.
                            </span>
                        ) : null}
                    </div>
                    {(group.items || []).map((item, itemIndex) => {
                        const numbers = appointmentsText(item.appointments);
                        const count = Array.isArray(item.appointments)
                            ? item.appointments.filter((n) => Number(n) > 0).length
                            : Number(item.appointments) || 0;
                        return (
                            <div className="aia-group__item" key={`${item.name}-${itemIndex}`}>
                                <span className="aia-group__name">
                                    {item.name}
                                    {count > 0 ? (
                                        <span className="aia-group__flag">
                                            приёмы: {numbers || count}
                                        </span>
                                    ) : (
                                        <span className="aia-group__flag is-warn">
                                            приёмы не отмечены
                                        </span>
                                    )}
                                </span>
                                <span className="aia-group__amount">
                                    {item.amount ? `${item.amount} ₽` : ""}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function ConfirmCard({ block, onConfirm, busy, done }) {
    const summary = Array.isArray(block.summary) ? block.summary : [];
    if (done) {
        return (
            <div className="aia-card aia-card--confirm is-done">
                <div className="aia-card__head">
                    <div className="aia-card__title">
                        <IoShieldCheckmarkOutline size={15} /> Подтверждено
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="aia-card aia-card--confirm">
            <div className="aia-card__head">
                <div className="aia-card__title">
                    <IoShieldCheckmarkOutline size={15} /> {block.title}
                </div>
            </div>
            {summary.length ? (
                <dl className="aia-fields">
                    {summary.map((row, index) => (
                        <div className="aia-fields__row" key={`${row.label}-${index}`}>
                            <dt>{row.label}</dt>
                            <dd>{row.value}</dd>
                        </div>
                    ))}
                </dl>
            ) : null}
            <div className="aia-confirm-actions">
                <button
                    type="button"
                    className="aia-confirm-btn aia-confirm-btn--ok"
                    disabled={busy}
                    onClick={() => onConfirm?.(true, block)}>
                    {block.confirm_text || "Подтвердить"}
                </button>
                <button
                    type="button"
                    className="aia-confirm-btn aia-confirm-btn--cancel"
                    disabled={busy}
                    onClick={() => onConfirm?.(false, block)}>
                    {block.cancel_text || "Отмена"}
                </button>
            </div>
        </div>
    );
}

function ScheduleList({ block }) {
    const items = Array.isArray(block.items) ? block.items : [];
    return (
        <div className="aia-card">
            <div className="aia-card__head">
                <div className="aia-card__title">
                    <IoCalendarOutline size={14} /> {block.title}
                </div>
                <div className="aia-card__subtitle">{block.date}</div>
            </div>
            {block.error ? <div className="aia-card__text">{block.error}</div> : null}
            {!block.error && items.length === 0 ? (
                <div className="aia-card__text">Записей нет</div>
            ) : null}
            <div className="aia-list">
                {items.map((item, index) => (
                    <div className="aia-row" key={`${item.id}-${index}`}>
                        <span className="aia-row__time">{item.time || "—"}</span>
                        <span className="aia-row__body">
                            <span className="aia-row__title">
                                {item.patient}
                                {item.in_mis === false ? (
                                    <span className="aia-row__flag">нет в программе</span>
                                ) : null}
                            </span>
                            <span className="aia-row__meta">
                                {[item.services, item.staff].filter(Boolean).join(" · ")}
                            </span>
                        </span>
                        <span className="aia-row__badge">{item.status}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AiAssistantBlocks({ blocks, onOpenPatient, onOpenPlan, onOpenDoc, onConfirm, confirmBusy, confirmedToken }) {
    if (!Array.isArray(blocks) || blocks.length === 0) return null;
    return (
        <div className="aia-blocks">
            {blocks.map((block, index) => {
                const key = `${block.type}-${index}`;
                switch (block.type) {
                    case "text":
                        return <TextBlock key={key} block={block} />;
                    case "patient_card":
                        return <PatientCard key={key} block={block} />;
                    case "patient_list":
                        return (
                            <PatientList
                                key={key}
                                block={block}
                                onOpenPatient={onOpenPatient}
                            />
                        );
                    case "doc_list":
                        return <DocList key={key} block={block} onOpenDoc={onOpenDoc} />;
                    case "analysis_list":
                        return <AnalysisList key={key} block={block} />;
                    case "records":
                        return <Records key={key} block={block} />;
                    case "plan_list":
                        return (
                            <PlanList key={key} block={block} onOpenPlan={onOpenPlan} />
                        );
                    case "plan":
                        return <PlanView key={key} block={block} />;
                    case "schedule":
                        return <ScheduleList key={key} block={block} />;
                    case "steps":
                        return <AiAssistantStepsDone key={key} items={block.items} />;
                    case "confirm":
                        return (
                            <ConfirmCard
                                key={key}
                                block={block}
                                onConfirm={onConfirm}
                                busy={confirmBusy}
                                done={Boolean(confirmedToken) && confirmedToken === block.token}
                            />
                        );
                    default:
                        return null;
                }
            })}
        </div>
    );
}
